import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Chip,
  CircularProgress,
  Pagination as MuiPagination,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoIcon from '@mui/icons-material/Info';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedIcon from '@mui/icons-material/Verified';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ListPageLayout from '../components/shared/list-page-layout';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { TableSkeleton } from '../components/shared/skeletons';
import { IListBiddingRequest } from '../../model/base/request/list-bidding-request';
import { useBiddingController } from './context/bidding-controller';
import { useBiddingState } from './context/bidding-context';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import { formatDate, getBiddingStatusColor } from 'utils';

export function BiddingComponent() {
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadData, loadModalities } = useBiddingController();
  const { biddings, modalities, loading, loadingFilters } = useBiddingState();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<IListBiddingRequest>({
    page: 0,
    pageSize: 12,
    term: '',
    modalityId: undefined,
    statusId: undefined,
  });

  // Carregar modalidades ao montar o componente
  useEffect(() => {
    loadModalities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregar dados quando os filtros mudarem
  useEffect(() => {
    loadData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.pageSize, filters.modalityId, filters.statusId, filters.term]);

  // Opções de modalidade (do backend via BaseAppService)
  const modalityOptions = modalities.map((modality) => ({
    value: modality.id || 0,
    label: modality.name || '',
  }));

  // Opções de status (baseado nos enums do backend)
  const statusOptions = [
    { value: 1, label: 'EM ANDAMENTO' },
    { value: 2, label: 'ANULADA' },
    { value: 3, label: 'CANCELADA' },
    { value: 4, label: 'DESERTA' },
    { value: 5, label: 'ENCERRADA' },
    { value: 6, label: 'FRUSTRADA' },
    { value: 7, label: 'HOMOLOGADA' },
    { value: 8, label: 'SUSPENSA' },
    { value: 9, label: 'REVOGADA' },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por número ou objeto',
      fullWidth: false,
    },
    {
      name: 'modalityId',
      label: 'Modalidade',
      type: 'select',
      options: modalityOptions,
      placeholder: 'Selecione uma modalidade',
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      options: statusOptions,
      placeholder: 'Selecione um status',
    },
  ];

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    const searchFilters = { ...filters, page: 0 };
    setCurrentPage(0);
    setFilters(searchFilters);
    loadData(searchFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: IListBiddingRequest = {
      page: 0,
      pageSize: 12,
      term: '',
      modalityId: undefined,
      statusId: undefined,
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    loadData(clearedFilters);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadData(newFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewDetails = (bidding: any) => {
    if (bidding?.externalUrl) {
      window.open(bidding.externalUrl, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/licitacoes/detalhes/${bidding?.id}`);
    }
  };

  return (
    <ListPageLayout
      title="Licitações"
      subtitle="Consulte as licitações do município"
      breadcrumbs={[
        { label: 'Licitações' },
      ]}
      filters={
        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof IListBiddingRequest],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={loading || loadingFilters}
        />
      }
    >
      {loading && !biddings.hasItems() ? (
        <TableSkeleton rows={12} columns={6} hasActions={true} />
      ) : !biddings.hasItems() ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhuma licitação encontrada com os filtros aplicados.
        </Alert>
      ) : (
        <>
          <Box sx={{ position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            )}
            <TableContainer
              component={Paper}
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                opacity: loading ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: 'primary.main',
                    }}
                  >
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '60px',
                      }}
                    >
                      <GavelIcon sx={{ verticalAlign: 'middle' }} />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '10%',
                      }}
                    >
                      Número
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                      }}
                    >
                      Objeto
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '15%',
                      }}
                    >
                      Modalidade
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '12%',
                      }}
                    >
                      Data Abertura
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '12%',
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        width: '80px',
                        textAlign: 'center',
                      }}
                    >
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {biddings.items.map((bidding) => (
                    <TableRow
                      key={bidding.id}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'action.hover',
                        },
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                        transition: 'background-color 0.2s ease',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleViewDetails(bidding)}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BusinessIcon
                            sx={{
                              fontSize: 32,
                              color: 'primary.main',
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {bidding.number || '-'}
                        </Typography>
                        {bidding.externalUrl && (
                          <Chip
                            icon={<VerifiedIcon />}
                            label="PNCP"
                            size="small"
                            color="success"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.5,
                          }}
                        >
                          {bidding.object || '-'}
                        </Typography>
                        {bidding.managementUnit && (
                          <Typography variant="caption" color="text.disabled" display="block">
                            {bidding.managementUnit}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={bidding.modality || '-'}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(bidding.openedOn)}
                        </Typography>
                        {bidding.limitDate && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Limite: {formatDate(bidding.limitDate)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={bidding.statusDescripton || bidding.status || '-'}
                          size="small"
                          color={getBiddingStatusColor(bidding.statusDescripton || bidding.status)}
                        />
                        {bidding.formattedValue && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {bidding.formattedValue}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'center',
                          }}
                        >
                          <Tooltip title={bidding.externalUrl ? "Abrir no PNCP" : "Ver Detalhes"} arrow>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleViewDetails(bidding)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'info.main',
                                  color: 'white',
                                },
                              }}
                            >
                              {bidding.externalUrl ? <OpenInNewIcon /> : <InfoIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {biddings.pageCount > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              <MuiPagination
                count={biddings.pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
    </ListPageLayout>
  );
}
